import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import { BankReconciliationService } from '../../services/bankReconciliation.service';
import {
  BANK_REC_STATUS_COLORS, BankLedgerLine, BankReconciliationWorkspace as WorkspaceData,
  BankStatementLine, BankStatementLineCreateDTO, MatchSuggestionReason,
} from '../../interfaces/bank/bankReconciliation.interface';
import { formatDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';
import AdjustmentDialog from './AdjustmentDialog';
// ---- FW-4A bank rules ----
import ApplyRulesDialog from './ApplyRulesDialog';
import BankRuleEditorDialog from './BankRuleEditorDialog';
import { ACTION_LABEL, BankRuleApplication, BankRulePreviewRow, BankRuleRequest } from '../../interfaces/bank/bankRule.interface';
import { BankRuleService } from '../../services/bankRule.service';
// ---- end FW-4A ----

const formatAmount = (value?: number) =>
  (value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SUGGESTION_LABEL: Record<MatchSuggestionReason, string> = {
  REFERENCE: 'Reference',
  EXACT_DATE: 'Same date',
  DATE_WINDOW: '±3 days',
  CHEQUE: 'Cheque no.',
};

interface LineFormState {
  lineDate: string;
  description: string;
  amount: string;
  statementBalance: string;
  externalRef: string;
}

const emptyLineForm: LineFormState = { lineDate: '', description: '', amount: '', statementBalance: '', externalRef: '' };

// ---- FW-4A: a draft rule from a statement line: its first significant words, its direction, its bank account ----
export const ruleDraftFromLine = (line: BankStatementLine, bankAccountId: string): Partial<BankRuleRequest> => {
  const words = (line.description ?? '').split(/[\s,;:/\\|()]+/)
    .filter(w => w.length >= 3 && !/\d/.test(w))
    .slice(0, 2);
  const text = words.length > 0 ? words.join(' ') : (line.description ?? '').trim().slice(0, 30);
  return {
    name: text ? `Rule: ${text}` : '',
    scopeId: bankAccountId,
    matchMode: 'ALL',
    conditions: [
      { field: 'DESCRIPTION', operator: 'CONTAINS', valueText: text },
      { field: 'DIRECTION', operator: 'IS', valueText: line.amount < 0 ? 'OUT' : 'IN' },
    ],
  };
};
// ---- end FW-4A ----

/** Line add/edit form -- inline dialog, not a separate exported component (T9 covers only
 * BankReconciliationWorkspace.tsx and AdjustmentDialog.tsx per the brief). */
const StatementLineDialog: React.FC<{
  initial?: BankStatementLine;
  onClose: () => void;
  onSave: (dto: BankStatementLineCreateDTO) => Promise<void>;
}> = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState<LineFormState>(initial ? {
    lineDate: initial.lineDate,
    description: initial.description || '',
    amount: String(initial.amount),
    statementBalance: initial.statementBalance !== undefined ? String(initial.statementBalance) : '',
    externalRef: initial.externalRef || '',
  } : emptyLineForm);
  const [saving, setSaving] = useState(false);

  const valid = form.lineDate.trim().length > 0 && form.amount.trim().length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        lineDate: form.lineDate,
        description: form.description.trim() || undefined,
        amount: Number(form.amount),
        statementBalance: form.statementBalance.trim() ? Number(form.statementBalance) : undefined,
        externalRef: form.externalRef.trim() || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.recLine.page' } as any}>
      <DialogTitle>{initial ? 'Edit Line' : 'Add Line'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }} data-help-section="bank.recLine.section.line">
        <TextField data-help="bank.recLine.field.date" label="Date" type="date" InputLabelProps={{ shrink: true }} required
          value={form.lineDate} onChange={e => setForm({ ...form, lineDate: e.target.value })} />
        <TextField data-help="bank.recLine.field.description" label="Description" value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })} />
        <TextField data-help="bank.recLine.field.amount" label="Amount" type="number" required value={form.amount}
          onChange={e => setForm({ ...form, amount: e.target.value })} />
        <TextField data-help="bank.recLine.field.balance" label="Statement Balance" type="number" value={form.statementBalance}
          onChange={e => setForm({ ...form, statementBalance: e.target.value })} />
        <TextField data-help="bank.recLine.field.reference" label="Reference" value={form.externalRef}
          onChange={e => setForm({ ...form, externalRef: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.recLine.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.recLine.save" variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

/** The reconciliation workbench (T9) -- driven entirely by BankReconciliationService.getWorkspace(id);
 * every mutation awaits a refetch so suggestions and the tie-out recompute server-side rather than
 * being derived client-side (except the selection-bar preview sum, which is informational only --
 * the server's 400 is the authority on whether a match is postable). */
const BankReconciliationWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [data, setData] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatementIds, setSelectedStatementIds] = useState<Set<string>>(new Set());
  const [selectedLedgerIds, setSelectedLedgerIds] = useState<Set<string>>(new Set());
  const [lineDialog, setLineDialog] = useState<{ mode: 'add' | 'edit'; line?: BankStatementLine } | null>(null);
  const [adjustmentLine, setAdjustmentLine] = useState<BankStatementLine | null>(null);
  // ---- FW-4A ----
  const { hasPermission } = useAuth();
  const canDefineRules = hasPermission(PERMISSIONS.BANK_RULE_MANAGE);
  const [applyRulesOpen, setApplyRulesOpen] = useState(false);
  const [ruleDraft, setRuleDraft] = useState<Partial<BankRuleRequest> | null>(null);
  // ---- FW-4B: rule chips on unmatched lines (preview data, no posting) and the applied-by-rule log for the unmatch confirm ----
  const canApplyRules = hasPermission(PERMISSIONS.BANK_RECONCILIATION_MANAGE);
  const hintsOnRef = useRef(false);
  const [ruleHints, setRuleHints] = useState<Record<string, BankRulePreviewRow>>({});
  const [ruleApps, setRuleApps] = useState<Record<string, BankRuleApplication>>({});
  const [hintsOn, setHintsOn] = useState(false);
  const [unmatchTarget, setUnmatchTarget] = useState<BankStatementLine | null>(null);
  // ---- end FW-4B ----
  // ---- end FW-4A ----
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const ws = await BankReconciliationService.getWorkspace(id);
      setData(ws);
      setSelectedStatementIds(new Set());
      setSelectedLedgerIds(new Set());
      // ---- FW-4B: best effort; a failure just means no chips and no rule-specific unmatch wording ----
      if (canApplyRules) {
        try {
          const apps = await BankRuleService.applications(id);
          const byLine: Record<string, BankRuleApplication> = {};
          (apps ?? []).filter(a => a.status === 'APPLIED').forEach(a => { byLine[a.statementLineId] = a; });
          setRuleApps(byLine);
        } catch { setRuleApps({}); }
        // the preview evaluates every unmatched line with every rule: only on demand, and after an import
        if (hintsOnRef.current && ws.reconciliation.status !== 'COMPLETED') {
          try {
            const p = await BankRuleService.preview(id);
            const byLine: Record<string, BankRulePreviewRow> = {};
            (p?.rows ?? []).forEach(r => { byLine[r.lineId] = r; });
            setRuleHints(byLine);
          } catch { setRuleHints({}); }
        } else {
          setRuleHints({});
        }
      }
      // ---- end FW-4B ----
    } catch (e) {
      toast.showApiError(e, 'Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [id, canApplyRules]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!id || !data) return null;

  const { reconciliation, bankAccount, statementLines, unmatchedLedgerLines, suggestions, tieOut } = data;
  const locked = reconciliation.status === 'COMPLETED';

  const suggestionFor = (lineId: string) => suggestions.find(s => s.bankStatementLineId === lineId);
  const suggestedLedgerIds = new Set(suggestions.map(s => s.journalEntryLineId));

  const allMatched = statementLines.length > 0 && statementLines.every(l => l.matched === true);
  const canComplete = !locked && tieOut.difference === 0 && allMatched;

  const toggleStatement = (lineId: string) => {
    setSelectedStatementIds(prev => {
      const next = new Set(prev);
      next.has(lineId) ? next.delete(lineId) : next.add(lineId);
      return next;
    });
  };
  const toggleLedger = (lineId: string) => {
    setSelectedLedgerIds(prev => {
      const next = new Set(prev);
      next.has(lineId) ? next.delete(lineId) : next.add(lineId);
      return next;
    });
  };

  const selectedStatementLines = statementLines.filter(l => selectedStatementIds.has(l.id));
  const selectedLedgerLines = unmatchedLedgerLines.filter(l => selectedLedgerIds.has(l.journalEntryLineId));
  const statementSelSum = selectedStatementLines.reduce((sum, l) => sum + l.amount, 0);
  const ledgerSelSum = selectedLedgerLines.reduce(
    (sum, l) => sum + (l.entryType === 'DEBIT' ? l.amount : -l.amount), 0);
  const selectionDiff = statementSelSum - ledgerSelSum;

  const handleImportClick = () => fileInputRef.current?.click();
  const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    try {
      const result = await BankReconciliationService.importStatement(id, file);
      toast.showSuccess(
        `${result.importedCount} imported, ${result.skippedDuplicateCount} duplicates skipped (${result.detectedFormat})`,
      );
      if (canApplyRules) { hintsOnRef.current = true; setHintsOn(true); }   // FW-4B: chips for the freshly imported lines
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to import statement');
    }
  };

  const handleAccept = async (line: BankStatementLine) => {
    const suggestion = suggestionFor(line.id);
    if (!suggestion) return;
    try {
      await BankReconciliationService.match(id, {
        bankStatementLineIds: [line.id],
        journalEntryLineIds: [suggestion.journalEntryLineId],
      });
      toast.showSuccess('Matched');
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to match line');
    }
  };

  const handleMatchSelected = async () => {
    try {
      await BankReconciliationService.match(id, {
        bankStatementLineIds: Array.from(selectedStatementIds),
        journalEntryLineIds: Array.from(selectedLedgerIds),
      });
      toast.showSuccess('Selected lines matched');
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to match selected lines');
    }
  };

  // ---- FW-4B: rule suggestions on demand (and automatically after an import) ----
  const handleShowSuggestions = async () => {
    hintsOnRef.current = true;
    setHintsOn(true);
    await load();
  };
  // ---- end FW-4B ----

  // ---- FW-4B: a rule-applied line gets a confirm that says what unmatching does to the document ----
  const handleUnmatchClick = (line: BankStatementLine) => {
    if (ruleApps[line.id]) setUnmatchTarget(line);
    else handleUnmatch(line);
  };
  // ---- end FW-4B ----

  const handleUnmatch = async (line: BankStatementLine) => {
    if (!line.matchGroupId) return;
    try {
      await BankReconciliationService.unmatch(id, { matchGroupId: line.matchGroupId });
      toast.showSuccess('Match removed');
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to unmatch line');
    }
  };

  const handleRemove = async (line: BankStatementLine) => {
    try {
      await BankReconciliationService.removeLine(line.id);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to remove line');
    }
  };

  const handleSaveLine = async (dto: BankStatementLineCreateDTO) => {
    try {
      if (lineDialog?.mode === 'edit' && lineDialog.line) {
        await BankReconciliationService.editLine(lineDialog.line.id, dto);
      } else {
        await BankReconciliationService.addLine(id, dto);
      }
      setLineDialog(null);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to save line');
    }
  };

  const handleComplete = async () => {
    try {
      await BankReconciliationService.complete(id);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to complete reconciliation');
    }
  };

  const handleReopen = async () => {
    try {
      await BankReconciliationService.reopen(id);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to reopen reconciliation');
    }
  };

  return (
    <Box data-help-page="banking.recWs.page" sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">{bankAccount.bankName} — {bankAccount.accountNumber}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {reconciliation.statementDate ? formatDate(reconciliation.statementDate) : '—'} ·{' '}
              {reconciliation.statementNumber || 'Draft'}
            </Typography>
            <Chip size="small" label={humanizeEnum(reconciliation.status)}
              color={BANK_REC_STATUS_COLORS[reconciliation.status] ?? 'default'} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <input ref={fileInputRef} type="file" accept=".csv,.ofx,.qfx,.sta,.mt940,.940,.swi,.txt,.xml" hidden onChange={handleImportChange} />
          <Button data-help="banking.recWs.import" variant="outlined" disabled={locked} onClick={handleImportClick}>Import Statement</Button>
          <Button data-help="banking.recWs.addLine" variant="outlined" disabled={locked} onClick={() => setLineDialog({ mode: 'add' })}>Add Line</Button>
          {/* ---- FW-4A ---- */}
          <Button data-help="banking.recWs.applyRules" variant="outlined" disabled={locked} onClick={() => setApplyRulesOpen(true)}>Apply bank rules</Button>
          {/* ---- end FW-4A ---- */}
          {canApplyRules && (
            <Button data-help="banking.recWs.suggestions" variant="outlined" disabled={locked} onClick={handleShowSuggestions}>
              {hintsOn ? 'Refresh rule suggestions' : 'Show rule suggestions'}
            </Button>
          )}
          <Button data-help="banking.recWs.complete" variant="contained" disabled={!canComplete} onClick={handleComplete}>Complete</Button>
          {locked && (
            <Button data-help="banking.recWs.reopen" variant="outlined" color="warning" onClick={handleReopen}>Reopen</Button>
          )}
        </Box>
      </Box>

      {!locked && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Statement import accepts CSV (date, description, amount, then optional balance and reference), OFX, SWIFT MT940 (.sta) or camt.053 (.xml). MT940 and camt.053 files must be for this bank account and currency.
          Dates can be yyyy-MM-dd or dd/MM/yyyy; amounts can include thousands commas, e.g. "-8,000.00".
        </Typography>
      )}

      {/* Sticky tie-out bar */}
      <Paper data-help-section="banking.recWs.tieOut"
        sx={{
          position: 'sticky', top: 0, zIndex: 1, p: 1.5, mb: 2,
          display: 'flex', flexWrap: 'wrap', gap: 3,
        }}
        variant="outlined"
      >
        <Typography variant="body2">Book: {formatAmount(tieOut.bookBalance)}</Typography>
        <Typography variant="body2">Outstanding: {formatAmount(tieOut.outstandingTotal)}</Typography>
        <Typography variant="body2">Reconciled: {formatAmount(tieOut.reconciledBalance)}</Typography>
        <Typography variant="body2">Statement closing: {formatAmount(tieOut.statementClosingBalance)}</Typography>
        <Typography variant="body2" sx={{ color: tieOut.difference === 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
          Difference: {formatAmount(tieOut.difference)}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Left pane: statement lines */}
        <Box sx={{ flex: '1 1 480px', minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Statement Lines</Typography>
          <TableContainer data-help-section="banking.recWs.statementTable" component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.recWs.stmt.col.select" padding="checkbox" />
                  <TableCell data-help="banking.recWs.stmt.col.date">Date</TableCell>
                  <TableCell data-help="banking.recWs.stmt.col.description">Description</TableCell>
                  <TableCell data-help="banking.recWs.stmt.col.reference">Reference</TableCell>
                  <TableCell data-help="banking.recWs.stmt.col.amount" align="right">Amount</TableCell>
                  <TableCell data-help="banking.recWs.stmt.col.state">State</TableCell>
                  <TableCell data-help="banking.recWs.stmt.col.actions">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statementLines.map(line => {
                  const suggestion = suggestionFor(line.id);
                  const grouped = (line.matchedJournalEntryLineIds?.length ?? 0) > 1;
                  return (
                    <TableRow key={line.id} data-testid={`statement-line-${line.id}`}>
                      <TableCell padding="checkbox">
                        <Checkbox data-help="banking.recWs.stmt.col.select"
                          inputProps={{ 'aria-label': `select-statement-${line.id}` }}
                          disabled={locked}
                          checked={selectedStatementIds.has(line.id)}
                          onChange={() => toggleStatement(line.id)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(line.lineDate)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description || '—'}</TableCell>
                      <TableCell>{line.externalRef || '—'}</TableCell>
                      <TableCell align="right">{formatAmount(line.amount)}</TableCell>
                      <TableCell>
                        {line.matched ? (
                          <Chip size="small" color="success" label="Matched" />
                        ) : suggestion ? (
                          <Chip size="small" color="info" label={SUGGESTION_LABEL[suggestion.reason]} />
                        ) : (
                          <Chip size="small" label="Unmatched" />
                        )}
                        {/* ---- FW-4B: suggestion chip ---- */}
                        {!line.matched && ruleHints[line.id] && (
                          <Tooltip title={`${ACTION_LABEL[ruleHints[line.id].actionType] ?? ruleHints[line.id].actionType}${ruleHints[line.id].targetLabel || ruleHints[line.id].accountLabel ? ' · ' + (ruleHints[line.id].targetLabel || ruleHints[line.id].accountLabel) : ''}${ruleHints[line.id].warnings.length ? ' — ' + ruleHints[line.id].warnings.join('; ') : ''}`}>
                            <Chip size="small" color={ruleHints[line.id].warnings.length ? 'warning' : 'secondary'} variant="outlined"
                              data-testid={`rule-chip-${line.id}`} sx={{ ml: 0.5 }} label={`Rule: ${ruleHints[line.id].ruleName}`} />
                          </Tooltip>
                        )}
                        {/* ---- end FW-4B ---- */}
                        {grouped && (
                          <Chip size="small" sx={{ ml: 0.5 }}
                            label={`grouped (${line.matchedJournalEntryLineIds!.length})`} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {!line.matched && suggestion && (
                            <Button data-help="banking.recWs.action.accept" size="small" disabled={locked} onClick={() => handleAccept(line)}>Accept</Button>
                          )}
                          {!line.matched && (
                            <Button data-help="banking.recWs.action.adjust" size="small" disabled={locked} onClick={() => setAdjustmentLine(line)}>Adjust</Button>
                          )}
                          {/* ---- FW-4A ---- */}
                          {!line.matched && canDefineRules && (
                            <Button data-help="banking.recWs.action.createRule" size="small" disabled={locked} onClick={() => setRuleDraft(ruleDraftFromLine(line, reconciliation.bankAccountId))}>
                              Create rule
                            </Button>
                          )}
                          {/* ---- end FW-4A ---- */}
                          {line.matched && (
                            <Button data-help="banking.recWs.action.unmatch" size="small" disabled={locked} onClick={() => handleUnmatchClick(line)}>Unmatch</Button>
                          )}
                          {!line.matched && (
                            <>
                              <Button data-help="banking.recWs.action.edit" size="small" disabled={locked} onClick={() => setLineDialog({ mode: 'edit', line })}>Edit</Button>
                              <Button data-help="banking.recWs.action.remove" size="small" color="error" disabled={locked} onClick={() => handleRemove(line)}>Remove</Button>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {statementLines.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">No statement lines yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Right pane: unmatched ledger lines */}
        <Box sx={{ flex: '1 1 420px', minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Unmatched Ledger Lines</Typography>
          <TableContainer data-help-section="banking.recWs.ledgerTable" component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.recWs.ledger.col.select" padding="checkbox" />
                  <TableCell data-help="banking.recWs.ledger.col.date">Date</TableCell>
                  <TableCell data-help="banking.recWs.ledger.col.entry">Entry #</TableCell>
                  <TableCell data-help="banking.recWs.ledger.col.description">Description</TableCell>
                  <TableCell data-help="banking.recWs.ledger.col.type">Type</TableCell>
                  <TableCell data-help="banking.recWs.ledger.col.amount" align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unmatchedLedgerLines.map((line: BankLedgerLine) => {
                  const highlighted = suggestedLedgerIds.has(line.journalEntryLineId);
                  return (
                    <TableRow
                      key={line.journalEntryLineId}
                      data-testid={`ledger-line-${line.journalEntryLineId}`}
                      sx={highlighted ? { bgcolor: 'action.hover' } : undefined}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox data-help="banking.recWs.ledger.col.select"
                          inputProps={{ 'aria-label': `select-ledger-${line.journalEntryLineId}` }}
                          disabled={locked}
                          checked={selectedLedgerIds.has(line.journalEntryLineId)}
                          onChange={() => toggleLedger(line.journalEntryLineId)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(line.date)}</TableCell>
                      <TableCell>{line.entryNumber}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description || '—'}</TableCell>
                      <TableCell>{line.entryType}</TableCell>
                      <TableCell align="right">{formatAmount(line.amount)}</TableCell>
                    </TableRow>
                  );
                })}
                {unmatchedLedgerLines.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">No unmatched ledger lines.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Selection bar -- many-to-one affordance */}
      <Paper
        sx={{
          position: 'sticky', bottom: 0, mt: 2, p: 1.5,
          display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
        }}
        variant="outlined"
      >
        <Typography variant="body2">Statement Σ {formatAmount(statementSelSum)}</Typography>
        <Typography variant="body2">Ledger Σ {formatAmount(ledgerSelSum)}</Typography>
        <Typography variant="body2" sx={{ color: selectionDiff === 0 ? 'text.primary' : 'error.main', fontWeight: 600 }}>
          Δ {formatAmount(selectionDiff)}
        </Typography>
        <Button data-help="banking.recWs.matchSelected"
          variant="contained"
          disabled={locked || selectedStatementIds.size === 0 || selectedLedgerIds.size === 0}
          onClick={handleMatchSelected}
        >
          Match selected ({selectedStatementIds.size} ↔ {selectedLedgerIds.size})
        </Button>
      </Paper>

      {lineDialog && (
        <StatementLineDialog
          initial={lineDialog.line}
          onClose={() => setLineDialog(null)}
          onSave={handleSaveLine}
        />
      )}
      {/* ---- FW-4A ---- */}
      {applyRulesOpen && (
        <ApplyRulesDialog
          reconciliationId={id}
          onClose={() => setApplyRulesOpen(false)}
          onApplied={() => { load(); }}
        />
      )}
      {ruleDraft && (
        <BankRuleEditorDialog
          initial={ruleDraft}
          onClose={() => setRuleDraft(null)}
          onSaved={() => { setRuleDraft(null); }}
        />
      )}
      {/* ---- end FW-4A ---- */}
      {/* ---- FW-4B: unmatch confirm for a rule-applied line ---- */}
      {unmatchTarget && (
        <Dialog open onClose={() => setUnmatchTarget(null)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.recUnmatch.page' } as any}>
          <DialogTitle>Unmatch this line?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" data-testid="unmatch-confirm-text">
              {ruleApps[unmatchTarget.id]?.actionType === 'POST_TO_ACCOUNT'
                ? 'This line was posted by a bank rule. Unmatching reverses the journal the rule posted, and the line returns to unmatched so a rule can be applied again.'
                : `This line was matched by a bank rule that created a ${(ACTION_LABEL[ruleApps[unmatchTarget.id]?.actionType] ?? 'document').toLowerCase()}. Unmatching removes the match only: the document stays. Reverse the receipt, payment or sale from its own page if it was wrong. Applying a rule to this line again would create a second document.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button data-help="bank.recUnmatch.keep" onClick={() => setUnmatchTarget(null)}>Keep match</Button>
            <Button data-help="bank.recUnmatch.confirm" variant="contained" color="warning" onClick={() => { const l = unmatchTarget; setUnmatchTarget(null); handleUnmatch(l); }}>Unmatch</Button>
          </DialogActions>
        </Dialog>
      )}
      {/* ---- end FW-4B ---- */}
      {adjustmentLine && (
        <AdjustmentDialog
          reconciliationId={id}
          line={adjustmentLine}
          onClose={() => setAdjustmentLine(null)}
          onPosted={() => { setAdjustmentLine(null); load(); }}
        />
      )}
    </Box>
  );
};

export default BankReconciliationWorkspace;
